/**
 * 分析マトリクスの「軸エンジン」共有モデル (動的・モード非依存)。
 *
 * 背景: マトリクスタブ (`AnalysisMatrixView`) は master / dataSettings.postFields / M5 ルール /
 * 分類 (classification) 由来の豊富な軸候補と、投稿単位 / AI 認識物 (cls) 単位のデータソース切替、
 * bucketMethod 6 種を備える動的な軸エンジンを持っていた。一方で分析(詳細選択)画面
 * (`AnalysisListView`) は感情 / 対象大分類 / postField のみを扱う簡易版を **別実装** しており、
 * マトリクスから遷移しても同じ軸・同じデータソースを再現できなかった (ハードコード重複)。
 *
 * 本モジュールは両画面が **同一の軸エンジン** を共有できるよう、AnalysisMatrixView 内に
 * インライン定義されていた純粋関数群をそのまま切り出したもの。マトリクスタブと分析画面の
 * どちらも、ここから軸候補の導出 / バケット化 / データソース展開 (cls/projected) を行う。
 *
 * 重要: 関数の挙動はマトリクスタブ移植元と完全一致させること (マトリクス回帰テストで担保)。
 */
import {
  findMasterRowByCode,
  type AiOutputFormat,
  type AnalysisAxisRule,
  type AnalysisDisplayRules,
  type FieldDef,
} from '@citizen-walk/shared-types';
import type { PostMinimal } from '../posts';
import {
  perspectiveAxisKey,
  perspectiveAxisLabel,
  projectForPerspective,
  resolvePerspectiveAxis,
  type AnalysisPerspective,
} from './analysisPerspectives';
import { resolveChipColor, type AiCategoryMastersResult } from '../posts';
import { CLS_AXIS_MIRROR_PREFIX, extractEcwAxisCodes } from './ecwAdapter';
import type { ClassificationRow } from './classificationAdapter';

/** 軸種別: 「{templateVar root}.{level}」形式の文字列キー。`templateVar root` は templateVar から末尾の "Master" を除いた値 (例: 'emotion', 'category', 'gql', 'hzr')。 */
export type AxisKey = string;

/** セル内並び順の sort 軸 (old2 SortField)。 */
export type MatrixSortField = 'score' | 'rank' | 'date';
/** sort 方向 (old2 SortOrder)。 */
export type MatrixSortOrder = 'asc' | 'desc';

/** score / rank フィルタの既定値 (old2 L42-43)。 */
export const DEFAULT_SCORE_FILTER = [-3, -2, -1, 1, 2, 3];

/**
 * 「（該当なし）」バケットの sentinel code / ラベル / 色。
 * 軸 (M5 rule) で includeEmpty=true のとき、その軸に値を持たない投稿/AI認識物を脱落させず
 * この受け皿バケットに集める (実コードと衝突しないよう `__none__` を使う)。
 */
export const EMPTY_AXIS_CODE = '__none__';
export const EMPTY_AXIS_LABEL = '（該当なし）';

/**
 * post (実 post or cls 擬似 post) の符号付きスコアを extraFields から解決する。
 * cls モードでは clsRowToPost が extraFields.score / assessmentScore を mirror する。
 * 実 post では dataSettings 由来の score 系 field が無いこともあるため null 許容。
 */
export function readPostScore(post: PostMinimal): number | null {
  return (
    readNumber(post.extraFields, 'score') ?? readNumber(post.extraFields, 'assessmentScore') ?? null
  );
}

/** post の rank を extraFields から解決 (cls モードのみ意味を持つ)。 */
export function readPostRank(post: PostMinimal): number | null {
  return readNumber(post.extraFields, 'rank');
}

/** post の作成日時を ms に解決 (sort 用。Timestamp 不在は 0)。 */
export function readPostMillis(post: PostMinimal): number {
  return post.createdAt ? post.createdAt.toMillis() : 0;
}

const DEFAULT_LEVEL_LABEL: Record<string, string> = {
  major: '大分類',
  category: '詳細',
  categoryCd: '詳細コード',
};

/**
 * templateVar 表示用ラベル fallback (動的管理拡張)。
 * master.displayLabelJa 未指定時に root key からの語幹辞書 fallback として使う。
 */
const TEMPLATE_VAR_LABEL_JA: Record<string, string> = {
  emotion: '感情',
  category: 'カテゴリ',
  gql: 'GQL',
  hzr: '災害',
};

/** templateVar (例 'emotionMaster') → root (例 'emotion')。 */
function templateVarRoot(templateVar: string): string {
  return templateVar.replace(/Master$/, '');
}

/** templateVar root + level から axisKey を組み立てる (例: 'emotion' + 'major' = 'emotion.major')。 */
function makeAxisKey(root: string, level: 'major' | 'category' | 'categoryCd'): string {
  return `${root}.${level}`;
}

/** root.level 形式 (master 由来軸) か。 */
export function isDefaultAxisKey(k: string): boolean {
  // root.level 形式 (例: emotion.major / category.categoryCd / 任意の master root)
  // 'postField:' prefix は dataSettings 由来軸として別経路で扱う。
  if (k.startsWith('postField:')) return false;
  return /^[a-zA-Z]+\.(major|category|categoryCd)$/.test(k);
}

/** dataSettings 由来軸 key の prefix 判定。 */
export function isPostFieldAxisKey(k: string): boolean {
  return k.startsWith('postField:');
}

/** `postField:<fieldKey>` から fieldKey を抜き出す。 */
export function postFieldAxisFieldKey(k: string): string {
  return k.slice('postField:'.length);
}

export interface AxisGroup {
  /** ユニーク key (majorCd or categoryCd or category 文字列 or custom bucket label)。 */
  code: string;
  /** 表示名 (例: 'ポジティブ' / '喜び' / '空間' / '広場・公園' / '中立' [custom range])。 */
  label: string;
  /** chip 配色 (master.colorMap or row.color or fallback slate)。 */
  color: string;
}

/** 段階スケール軸のバケット数上限 (壊れた min/max 定義でセルが無限に増えるのを防ぐ)。 */
export const RATING_SCALE_MAX_BUCKETS = 21;

export const CUSTOM_RANGE_COLORS = [
  '#ef4444',
  '#f59e0b',
  '#10b981',
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
];

/* ===== 段階スケール (rating-scale) の値別配色 ===== */

/**
 * 段階スケールの chip 配色は **正負で分ける**。地図の凡例 (`buildRatingScaleColorRule`) /
 * 投稿詳細の値チップと同じ 3 色を基準色に採り、同じ設問なら画面をまたいでも
 * 「赤 = 負 / 緑 = 正 / グレー = 0」が一貫して読めるようにする。
 */
const RATING_SCALE_NEGATIVE_COLOR = '#EF4444';
const RATING_SCALE_ZERO_COLOR = '#9CA3AF';
const RATING_SCALE_POSITIVE_COLOR = '#10B981';

/** `#RRGGBB` を黒方向に `ratio` (0..1) だけ寄せる。段階の「強さ」を濃さで表すために使う。 */
function darkenHex(hex: string, ratio: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1] as string, 16);
  const k = Math.max(0, Math.min(1, 1 - ratio));
  const ch = (shift: number) => Math.round(((n >> shift) & 0xff) * k);
  const out = (ch(16) << 16) | (ch(8) << 8) | ch(0);
  return `#${out.toString(16).padStart(6, '0')}`;
}

/**
 * 段階スケールの 1 値 → chip 色。
 *
 * - 符号で色相を分ける (負 = 赤系 / 0 = グレー / 正 = 緑系)
 * - |値| が 1 のときが基準色、端に向かうほど濃くする (最大 40% 暗く)
 *   (「薄くする」方向に振らないのは、淡色チップが背景に埋もれて段階が読めなくなるため)
 */
export function ratingScaleValueColor(value: number, min: number, max: number): string {
  if (value === 0) return RATING_SCALE_ZERO_COLOR;
  const base = value > 0 ? RATING_SCALE_POSITIVE_COLOR : RATING_SCALE_NEGATIVE_COLOR;
  const extent = Math.max(1, Math.abs(value > 0 ? max : min));
  // |値|=1 → 0、|値|=extent → 0.4 の線形補間 (extent が 1 なら常に 0 = 基準色)。
  const step = extent > 1 ? (Math.abs(value) - 1) / (extent - 1) : 0;
  return darkenHex(base, step * 0.4);
}

/** 段階スケールの 1 値 → chip ラベル (正値は `+N`)。両端だけ scaleLabels を併記する。 */
export function ratingScaleValueLabel(
  value: number,
  min: number,
  max: number,
  scaleLabels: { posLabel?: string; negLabel?: string } | undefined,
): string {
  const num = value > 0 ? `+${value}` : String(value);
  const posLabel = scaleLabels?.posLabel?.trim();
  const negLabel = scaleLabels?.negLabel?.trim();
  if (value === max && value > 0 && posLabel) return `${num} ${posLabel}`;
  if (value === min && value < 0 && negLabel) return `${num} ${negLabel}`;
  return num;
}

/**
 * 軸種別 + resolver から axis groups を抽出 (rows の出現順、code でユニーク化)。
 * `axisKey = "{templateVar root}.{level}"` 形式で動的解決。
 */
export function extractDefaultAxis(resolver: AiCategoryMastersResult, axis: AxisKey): AxisGroup[] {
  const dot = axis.indexOf('.');
  if (dot < 0) return [];
  const root = axis.slice(0, dot);
  const level = axis.slice(dot + 1) as 'major' | 'category' | 'categoryCd';
  const masterTemplateVar = `${root}Master`;
  const master = resolver.byTemplateVar.get(masterTemplateVar);
  if (!master) return [];
  const seen = new Map<string, AxisGroup>();
  for (const row of master.rows ?? []) {
    let code: string;
    let label: string;
    if (level === 'major') {
      code = row.majorCd ?? '_';
      label = row.major ?? code;
    } else if (level === 'category') {
      code = row.category ?? '_';
      label = row.category ?? code;
    } else {
      code = row.categoryCd ?? '_';
      label = row.category ?? code;
    }
    if (seen.has(code)) continue;
    seen.set(code, { code, label, color: resolveChipColor(row, master) });
  }
  return Array.from(seen.values());
}

/** includeEmpty 軸に「（該当なし）」グループを末尾追加 (既に在れば二重追加しない)。 */
export function appendEmptyBucket(groups: AxisGroup[], include: boolean): AxisGroup[] {
  if (!include || groups.some((g) => g.code === EMPTY_AXIS_CODE)) return groups;
  return [...groups, { code: EMPTY_AXIS_CODE, label: EMPTY_AXIS_LABEL, color: '#cbd5e1' }];
}

/**
 * load 済 master の templateVar から axis 候補を動的生成。
 * 各 master に対し 3 levels (major / category / categoryCd) を提供 (rows[] に該当値があるもののみ)。
 */
export function deriveAxisOptionsFromMasters(
  resolver: AiCategoryMastersResult,
): { key: string; label: string }[] {
  const opts: { key: string; label: string }[] = [];
  for (const master of resolver.masters ?? []) {
    if (!master.templateVar) continue;
    const root = templateVarRoot(master.templateVar);
    // 動的管理拡張: master.displayLabelJa を 1st 優先、TEMPLATE_VAR_LABEL_JA を 2nd、raw root を 3rd。
    const rootLabel = master.displayLabelJa?.trim() || TEMPLATE_VAR_LABEL_JA[root] || root;
    const hasMajor = (master.rows ?? []).some((r) => r.majorCd || r.major);
    const hasCategoryLabel = (master.rows ?? []).some((r) => r.category);
    const hasCategoryCd = (master.rows ?? []).some((r) => r.categoryCd);
    if (hasMajor) {
      opts.push({
        key: makeAxisKey(root, 'major'),
        label: `${rootLabel} (${DEFAULT_LEVEL_LABEL.major})`,
      });
    }
    if (hasCategoryLabel) {
      opts.push({
        key: makeAxisKey(root, 'category'),
        label: `${rootLabel} (${DEFAULT_LEVEL_LABEL.category})`,
      });
    }
    if (hasCategoryCd) {
      opts.push({
        key: makeAxisKey(root, 'categoryCd'),
        label: `${rootLabel} (${DEFAULT_LEVEL_LABEL.categoryCd})`,
      });
    }
  }
  return opts;
}

/** AnalysisAxisRule の custom-ranges からグループ抽出。 */
function extractCustomRangeAxis(rule: AnalysisAxisRule): AxisGroup[] {
  if (rule.bucketMethod !== 'custom-ranges' || !rule.customRanges) return [];
  return rule.customRanges.map((r, i) => ({
    code: r.label,
    label: r.label,
    color: CUSTOM_RANGE_COLORS[i % CUSTOM_RANGE_COLORS.length] ?? '#64748b',
  }));
}

/**
 * bucketMethod を正規化。`each-value` (旧 6 種目) は parser が drop するため undefined で届くが、
 * defensive に `auto` と同一扱い。未指定 / 不正値も `auto` に fallback (= 「落ちない」要件)。
 */
export type BucketMethod = NonNullable<AnalysisAxisRule['bucketMethod']>;
export function normalizeBucketMethod(rule: AnalysisAxisRule | null | undefined): BucketMethod {
  const m = rule?.bucketMethod;
  if (m === 'by-name' || m === 'mean-split' || m === 'positive-negative' || m === 'custom-ranges') {
    return m;
  }
  // 'auto' / undefined / 不明値 (将来 'each-value' が union 追加された場合も含む) → auto
  return 'auto';
}

const MEAN_SPLIT_GE = '≥';
const MEAN_SPLIT_LT = '<';
const POS_NEG_POS = '≥0（正）';
const POS_NEG_NEG = '<0（負）';

/** mean-split: extraFields[axisKey] 数値の平均を境に 2 bucket。 */
function extractMeanSplitAxis(
  rule: AnalysisAxisRule,
  posts: PostMinimal[],
  isVertical: boolean,
): AxisGroup[] {
  const values = posts
    .map((p) => readNumber(p.extraFields, rule.axisKey))
    .filter((v): v is number => v !== null);
  if (values.length === 0) return [];
  const avg = values.reduce((a, b) => a + b, 0) / values.length;
  const avgLabel = avg.toFixed(1);
  const ge: AxisGroup = {
    code: `ge:${avgLabel}`,
    label: `${MEAN_SPLIT_GE}${avgLabel}`,
    color: CUSTOM_RANGE_COLORS[3]!,
  };
  const lt: AxisGroup = {
    code: `lt:${avgLabel}`,
    label: `${MEAN_SPLIT_LT}${avgLabel}`,
    color: CUSTOM_RANGE_COLORS[0]!,
  };
  // 旧踏襲: 縦軸は大→小 (≥avg, <avg)、横軸は小→大 (<avg, ≥avg)
  return isVertical ? [ge, lt] : [lt, ge];
}

/** positive-negative: 0 を境に正負 2 bucket。 */
function extractPositiveNegativeAxis(isVertical: boolean): AxisGroup[] {
  const pos: AxisGroup = { code: 'pos', label: POS_NEG_POS, color: CUSTOM_RANGE_COLORS[2]! };
  const neg: AxisGroup = { code: 'neg', label: POS_NEG_NEG, color: CUSTOM_RANGE_COLORS[0]! };
  return isVertical ? [pos, neg] : [neg, pos];
}

/**
 * by-name (master 軸): master row の code を major/category 名に解決して bucket。
 * 既定軸 (`root.level`) のときのみ master 解決、それ以外は data 由来の値をユニーク化。
 */
function extractByNameMasterAxis(resolver: AiCategoryMastersResult, axisKey: string): AxisGroup[] {
  return extractDefaultAxis(resolver, axisKey);
}

/** by-name (postField / extraFields 軸): post の値をそのままユニーク化して bucket。 */
function extractByNameDataAxis(rule: AnalysisAxisRule, posts: PostMinimal[]): AxisGroup[] {
  const seen = new Set<string>();
  for (const p of posts) {
    const raw = p.extraFields?.[rule.axisKey];
    const value = raw == null ? '' : String(raw);
    if (value) seen.add(value);
  }
  return Array.from(seen)
    .sort()
    .map((value, i) => ({
      code: value,
      label: value,
      color: CUSTOM_RANGE_COLORS[i % CUSTOM_RANGE_COLORS.length] ?? '#64748b',
    }));
}

/**
 * rule 軸 (custom-ranges / mean-split / positive-negative / by-name) の AxisGroup[] を抽出。
 * by-name で既定 master 軸を指す場合は master 解決、それ以外は data 由来。
 */
export function extractRuleAxis(
  rule: AnalysisAxisRule,
  resolver: AiCategoryMastersResult,
  posts: PostMinimal[],
  isVertical: boolean,
): AxisGroup[] {
  const method = normalizeBucketMethod(rule);
  if (method === 'custom-ranges') return extractCustomRangeAxis(rule);
  if (method === 'mean-split') return extractMeanSplitAxis(rule, posts, isVertical);
  if (method === 'positive-negative') return extractPositiveNegativeAxis(isVertical);
  if (method === 'by-name') {
    return isDefaultAxisKey(rule.axisKey)
      ? extractByNameMasterAxis(resolver, rule.axisKey)
      : extractByNameDataAxis(rule, posts);
  }
  return [];
}

/** rule 軸の post → code 判定 (mean-split / positive-negative / by-name / custom-ranges)。 */
function postToRuleCodes(post: PostMinimal, rule: AnalysisAxisRule): Set<string> {
  const codes = new Set<string>();
  const method = normalizeBucketMethod(rule);

  if (method === 'custom-ranges' && rule.customRanges) {
    const value = readNumber(post.extraFields, rule.axisKey);
    if (value === null) return codes;
    for (const r of rule.customRanges) {
      if (value >= r.min && value <= r.max) codes.add(r.label);
    }
    return codes;
  }

  if (method === 'mean-split') {
    // group code は `ge:<avg>` / `lt:<avg>`。avg は呼出側 (postToMeanSplitCodes) で解決する。
    return codes;
  }

  if (method === 'positive-negative') {
    const value = readNumber(post.extraFields, rule.axisKey);
    if (value === null) return codes;
    codes.add(value >= 0 ? 'pos' : 'neg');
    return codes;
  }

  if (method === 'by-name') {
    const raw = post.extraFields?.[rule.axisKey];
    const value = raw == null ? '' : String(raw);
    if (value) codes.add(value);
    return codes;
  }

  return codes;
}

/** mean-split は avg (group 集合から導出) が必要なため別関数。code = `ge:<avg>` / `lt:<avg>`。 */
export function postToMeanSplitCodes(
  post: PostMinimal,
  rule: AnalysisAxisRule,
  groups: AxisGroup[],
): Set<string> {
  const codes = new Set<string>();
  const value = readNumber(post.extraFields, rule.axisKey);
  if (value === null) return codes;
  // group.code は `ge:<avg>` / `lt:<avg>` の形。avg を 1 つ抜き出す。
  const sample = groups.find((g) => g.code.startsWith('ge:') || g.code.startsWith('lt:'));
  if (!sample) return codes;
  const avg = parseFloat(sample.code.slice(sample.code.indexOf(':') + 1));
  if (!Number.isFinite(avg)) return codes;
  codes.add(value >= avg ? `ge:${avg.toFixed(1)}` : `lt:${avg.toFixed(1)}`);
  return codes;
}

/* ===== cls データソース (AI 認識物 1 件 = 1 マス) ===== */

/** classification row → extraFields ミラー (assessment/score/rank + 各軸 code/name/major)。 */
export function mirrorRowToExtraFields(
  row: ClassificationRow,
  negativeLabel?: string,
): Record<string, unknown> {
  // 符号付き評価スコア (old2 MatrixView 踏襲): 負値はそのまま、正値で negativeLabel 一致なら反転。
  const signedAssessmentScore =
    row.score < 0
      ? row.score
      : negativeLabel && row.assessment === negativeLabel
        ? -row.score
        : row.score;
  const ef: Record<string, unknown> = {
    assessment: row.assessment,
    assessmentLevel: row.assessmentLevel,
    score: row.score,
    assessmentScore: signedAssessmentScore,
    rank: row.rank,
  };
  // 分類軸の code/name/major は予約 prefix 付き key に隔離する (postField 軸との衝突回避)。
  for (const [key, v] of Object.entries(row.axes)) {
    ef[`${CLS_AXIS_MIRROR_PREFIX}${key}`] = v.code;
    ef[`${CLS_AXIS_MIRROR_PREFIX}${key}Name`] = v.name;
    if (v.majorCode) ef[`${CLS_AXIS_MIRROR_PREFIX}${key}Major`] = v.majorCode;
  }
  return ef;
}

/**
 * ClassificationRow → 擬似 PostMinimal 変換 (cls 1 行 = 1 マス要素)。
 * id は `${postId}__cls${rank}` で投稿内一意化。
 */
export function clsRowToPost(
  row: ClassificationRow,
  negativeLabel?: string,
  parentExtra?: Record<string, unknown> | null,
): PostMinimal {
  // 親投稿の入力項目 (KeikanType 等) を継承し、その上に分類由来の値を重ねる (同名は分類由来優先)。
  const extraFields: Record<string, unknown> = {
    ...(parentExtra ?? {}),
    ...mirrorRowToExtraFields(row, negativeLabel),
  };

  return {
    // 列挙値ごとに複製された行は variantKey で区別する (`__cls` 以降は呼出側が捨てるので安全)。
    id: `${row.postId}__cls${row.rank}${row.variantKey ? `_${row.variantKey}` : ''}`,
    authorUid: '',
    authorNickname: row.postAuthor,
    title: row.reasoning || row.postTitle,
    comment: row.reasoning,
    location: null,
    accuracy: null,
    source: null,
    visibility: 'public',
    sessionId: row.postSessionId,
    createdAt: null,
    photoPath: row.postPhotoPath,
    photoThumbPath: row.postPhotoThumbPath,
    photoUrl: null,
    photoThumbUrl: null,
    agreeCount: 0,
    aiAnalysisStatus: 'done',
    aiAnalysisError: null,
    aiAnalysisEcw: {
      description: '',
      emotionText: '',
      emotions: [],
      emotionReason: '',
      categoryCodes: [],
      categoryReason: '',
    },
    aiAnalysisBousai: null,
    aiAnalysisWalker: null,
    aiAnalysisRaw: null,
    aiAnalysisAnalyzedAt: null,
    aiAnalysisSettingsId: null,
    aiAnalysisGeneric: null,
    extraFields,
  };
}

/* ===== dataSettings.postFields 由来軸 ===== */

export interface PostFieldAxisGroup extends AxisGroup {
  /** rating-scale / number 用の min/max range (post の値からこの bucket か判定)。 */
  range?: { min: number; max: number };
  /** select / multiselect 用の value 集合 (post の extraFields[key] と string 一致判定)。 */
  optionValues?: Set<string>;
}

export function buildPostFieldAxisGroups(field: FieldDef): PostFieldAxisGroup[] {
  if (field.type === 'number') {
    // 値域不明 — 「全件」1 bucket (簡易)
    return [
      {
        code: 'all',
        label: field.label || field.key,
        color: CUSTOM_RANGE_COLORS[0]!,
        range: { min: -Infinity, max: Infinity },
      },
    ];
  }
  if (field.type === 'rating-scale') {
    // 段階スケールは **1 段階 = 1 バケット** (マトリクスで「数値ごとのセル」になる)。
    // 旧実装は 負 / 0 / 正 の 3 バケットに畳んでいたため、-3 と -1 が同じセルに落ちて
    // 段階の分布がマトリクス上で読めなかった。色は正負で分ける方針を維持する
    // (ratingScaleValueColor: 負 = 赤系 / 0 = グレー / 正 = 緑系、端ほど濃い)。
    const min = field.optionsMeta?.min ?? -3;
    const max = field.optionsMeta?.max ?? 3;
    const excludeZero = field.optionsMeta?.excludeZero ?? false;
    const scaleLabels = field.optionsMeta?.scaleLabels;
    const groups: PostFieldAxisGroup[] = [];
    // 定義が壊れている (min > max) 地区でも落ちないよう、また段数が極端でも UI が破綻しないよう
    // 上限 (RATING_SCALE_MAX_BUCKETS) で打ち切る。
    for (let v = min; v <= max && groups.length < RATING_SCALE_MAX_BUCKETS; v++) {
      if (excludeZero && v === 0) continue;
      groups.push({
        code: String(v),
        label: ratingScaleValueLabel(v, min, max, scaleLabels),
        color: ratingScaleValueColor(v, min, max),
        range: { min: v, max: v },
      });
    }
    return groups;
  }
  if (field.type === 'select' || field.type === 'multiselect') {
    return (field.options ?? []).map((opt, i) => ({
      code: opt.value,
      label: opt.label || opt.value,
      color: CUSTOM_RANGE_COLORS[i % CUSTOM_RANGE_COLORS.length] ?? '#64748b',
      optionValues: new Set([opt.value]),
    }));
  }
  return [];
}

/**
 * 段階スケールのように **値の大小に意味がある軸** を、縦軸では上が大 (ポジティブ側)、
 * 下が小 (ネガティブ側) になるよう並べ替える。
 *
 * 表 (thead → tbody) は上から下へ描画されるので、`buildPostFieldAxisGroups` が返す
 * 昇順 (min→max) をそのまま縦軸に使うと上端が最もネガティブになってしまう。
 * M5 の rule 軸 (mean-split / positive-negative) が `isVertical` で「縦は大→小」に
 * 揃えているのと同じ方針に合わせる。横軸は左→右で小→大のまま。
 *
 * 選択肢の並び順そのものに意味がある select / multiselect は並べ替えない。
 */
export function orientPostFieldAxisGroups(
  field: FieldDef | null | undefined,
  groups: PostFieldAxisGroup[],
  isVertical: boolean,
): PostFieldAxisGroup[] {
  if (!isVertical || !field) return groups;
  if (field.type !== 'rating-scale' && field.type !== 'number') return groups;
  return [...groups].reverse();
}

/**
 * postFields から軸候補一覧を生成 (rating-scale / number / select / multiselect のみ)。
 * 各軸 key は `postField:<fieldKey>` prefix で master 由来軸と衝突回避。
 */
export function deriveAxisOptionsFromDataSettings(
  postFields: FieldDef[] | undefined,
): { key: string; label: string; field: FieldDef; groups: PostFieldAxisGroup[] }[] {
  if (!postFields || postFields.length === 0) return [];
  const out: { key: string; label: string; field: FieldDef; groups: PostFieldAxisGroup[] }[] = [];
  for (const field of postFields) {
    if (
      field.type !== 'rating-scale' &&
      field.type !== 'number' &&
      field.type !== 'select' &&
      field.type !== 'multiselect'
    ) {
      continue;
    }
    // useAsAnalysisAxis === false の場合は軸候補から除外 (admin が明示的に opt-out)
    if (field.useAsAnalysisAxis === false) continue;
    const groups = buildPostFieldAxisGroups(field);
    if (groups.length === 0) continue;
    out.push({
      key: `postField:${field.key}`,
      label: field.label || field.key,
      field,
      groups,
    });
  }
  return out;
}

/** postField 軸 (rating-scale / number / select / multiselect) で post の値が bucket に入るか判定。 */
function postFieldValueToCodes(
  post: PostMinimal,
  field: FieldDef,
  groups: PostFieldAxisGroup[],
): Set<string> {
  const codes = new Set<string>();
  const ef = post.extraFields;
  if (!ef) return codes;
  if (field.type === 'rating-scale' || field.type === 'number') {
    const raw = readNumber(ef, field.key);
    if (raw === null) return codes;
    // 段階スケールは 1 段階 = 1 バケット (range が単一値) なので、旧データ等で小数が入っていると
    // どのバケットにも入らず投稿が消える。最寄りの段階に丸めて必ずどこかのセルに落とす。
    const v = field.type === 'rating-scale' ? Math.round(raw) : raw;
    for (const g of groups) {
      if (g.range && v >= g.range.min && v <= g.range.max) {
        codes.add(g.code);
      }
    }
    return codes;
  }
  if (field.type === 'select') {
    const raw = ef[field.key];
    const value = typeof raw === 'string' ? raw : null;
    if (!value) return codes;
    for (const g of groups) {
      if (g.optionValues?.has(value)) codes.add(g.code);
    }
    return codes;
  }
  if (field.type === 'multiselect') {
    const raw = ef[field.key];
    const values: string[] = Array.isArray(raw)
      ? (raw.filter((x) => typeof x === 'string') as string[])
      : [];
    for (const v of values) {
      for (const g of groups) {
        if (g.optionValues?.has(v)) codes.add(g.code);
      }
    }
    return codes;
  }
  return codes;
}

export function readNumber(
  extraFields: Record<string, unknown> | null,
  key: string,
): number | null {
  if (!extraFields) return null;
  const v = extraFields[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * 同じ categoryCd 値が複数 master に存在するときの混線を解消するため、
 * templateVar 制約で master を絞り、その master の rows[] から code を探す。
 */
function lookupRowInMaster(
  resolver: AiCategoryMastersResult,
  templateVar: string,
  by: 'category' | 'categoryCd',
  value: string,
): { majorCd?: string; category?: string; categoryCd?: string } | undefined {
  const master = resolver.byTemplateVar.get(templateVar);
  if (!master) return undefined;
  if (by === 'category') {
    for (const row of master.rows ?? []) {
      if (row.category === value) return row;
    }
    return undefined;
  }
  // AI 出力コードは結合形 (例 "D-01") や接頭辞付き (例 "GQL-05") の場合がある。
  // bare categoryCd / 結合形 / 一意な接尾一致 を master スコープで照合する (findMasterRowByCode)。
  return findMasterRowByCode(master, value);
}

/**
 * post を軸種別に応じて該当 code 集合に変換 (1 post で複数 chip があれば 1 セル 1 件として加算)。
 * 既定 4 種は ECW emotions/categoryCodes (format 駆動)、custom axis は extraFields[axisKey] を bucket。
 */
export function postToCodes(
  post: PostMinimal,
  axis: AxisKey,
  resolver: AiCategoryMastersResult,
  customRule: AnalysisAxisRule | null,
  format: AiOutputFormat | null | undefined,
  postFieldAxisEntry?: { field: FieldDef; groups: PostFieldAxisGroup[] } | null,
): Set<string> {
  const codes = new Set<string>();
  // dataSettings 由来軸 → extraFields から bucket 化
  if (isPostFieldAxisKey(axis)) {
    if (!postFieldAxisEntry) return codes;
    return postFieldValueToCodes(post, postFieldAxisEntry.field, postFieldAxisEntry.groups);
  }
  if (isDefaultAxisKey(axis)) {
    // format が与えられていれば ecwAdapter 経由で軸 code を解決。
    // 旧 hardcode (aiAnalysisEcw 直参照) は format 未指定時のみ fallback として残置。
    if (format) {
      const rawCodes = extractEcwAxisCodes(post, axis, format, resolver);
      const dotIdx = axis.indexOf('.');
      const root = dotIdx > 0 ? axis.slice(0, dotIdx) : axis;
      const level = dotIdx > 0 ? axis.slice(dotIdx + 1) : '';
      // AI 出力値はテンプレート次第で「コード」(A-01 / GQL-05) にも「ラベル」(喜び) にもなる。
      // モード別の決め打ち (旧: root==='emotion' のときだけラベル照合) を廃し、
      // コード照合 → ラベル照合 の順で master 行を解決する (モード非依存)。
      const rowOf = (value: string) =>
        lookupRowInMaster(resolver, `${root}Master`, 'categoryCd', value) ??
        lookupRowInMaster(resolver, `${root}Master`, 'category', value);
      if (level === 'major') {
        for (const code of rawCodes) {
          const row = rowOf(code);
          if (row?.majorCd) codes.add(row.majorCd);
        }
      } else if (level === 'category') {
        for (const c of rawCodes) {
          const name = rowOf(c)?.category;
          codes.add(name != null && name !== '' ? name : c);
        }
      } else {
        // categoryCd 軸のグループ code は master の categoryCd (bare)。AI 出力が結合形 ("A-01") や
        // 接頭辞付き ("GQL-05")、ラベルのときも同じセルに集まるよう master で正規化する。
        for (const c of rawCodes) {
          const cd = rowOf(c)?.categoryCd;
          codes.add(cd != null && cd !== '' ? cd : c);
        }
      }
      return codes;
    }
    // format 未指定: 旧 v2 hardcode 経路 (後方互換) — master-aware 化済
    if (!post.aiAnalysisEcw) return codes;
    if (axis === 'emotion.major') {
      for (const label of post.aiAnalysisEcw.emotions) {
        const row = lookupRowInMaster(resolver, 'emotionMaster', 'category', label);
        if (row?.majorCd) codes.add(row.majorCd);
      }
    } else if (axis === 'emotion.category') {
      for (const label of post.aiAnalysisEcw.emotions) {
        codes.add(label);
      }
    } else if (axis === 'category.major') {
      for (const code of post.aiAnalysisEcw.categoryCodes) {
        const row = lookupRowInMaster(resolver, 'categoryMaster', 'categoryCd', code);
        if (row?.majorCd) codes.add(row.majorCd);
      }
    } else {
      // category.categoryCd
      for (const code of post.aiAnalysisEcw.categoryCodes) {
        codes.add(code);
      }
    }
    return codes;
  }

  // rule 軸 (custom-ranges / positive-negative / by-name)。
  // mean-split は avg が必要なため呼出側で postToMeanSplitCodes を使う。
  if (customRule) {
    return postToRuleCodes(post, customRule);
  }
  return codes;
}

/**
 * M5 matrix rules から custom axis の {key, label, rule} list を抽出。
 * - 既定 master 軸 (`root.level`) に付いた rule は extractDefaultAxis 経路で扱うため除外。
 * - custom-ranges は customRanges 空を除外。
 * - auto / each-value は専用 group が作れないため custom 軸候補にしない。
 */
export function collectCustomAxes(
  matrixRules: AnalysisDisplayRules['matrix'] | undefined,
): { key: string; label: string; rule: AnalysisAxisRule }[] {
  if (!matrixRules) return [];
  const rules: AnalysisAxisRule[] = [];
  if (matrixRules.vAxisRule) rules.push(matrixRules.vAxisRule);
  if (matrixRules.hAxisRule) rules.push(matrixRules.hAxisRule);
  if (matrixRules.additionalAxisRules) rules.push(...matrixRules.additionalAxisRules);
  const seen = new Set<string>();
  const out: { key: string; label: string; rule: AnalysisAxisRule }[] = [];
  for (const r of rules) {
    if (isDefaultAxisKey(r.axisKey)) continue;
    const method = normalizeBucketMethod(r);
    if (method === 'auto') continue; // auto / each-value は専用 bucket を持たない
    if (method === 'custom-ranges' && (!r.customRanges || r.customRanges.length === 0)) continue;
    if (seen.has(r.axisKey)) continue;
    seen.add(r.axisKey);
    out.push({ key: r.axisKey, label: r.axisKey, rule: r });
  }
  return out;
}

/** M5 の vAxisRule / hAxisRule / additionalAxisRules で includeEmpty=true の axisKey 集合。 */
export function collectIncludeEmptyKeys(
  matrixRules: AnalysisDisplayRules['matrix'] | undefined,
): Set<string> {
  const s = new Set<string>();
  const rules = [
    matrixRules?.vAxisRule,
    matrixRules?.hAxisRule,
    ...(matrixRules?.additionalAxisRules ?? []),
  ];
  for (const r of rules) if (r?.includeEmpty && r.axisKey) s.add(r.axisKey);
  return s;
}

/**
 * セル内 post の並び替え (old2 MatrixView のセル内並び順設定)。
 * 値が無い post は末尾に寄せる (sort 安定性のため index で tie-break)。
 */
export function sortCellPosts(
  list: PostMinimal[],
  field: MatrixSortField,
  order: MatrixSortOrder,
): PostMinimal[] {
  const dir = order === 'asc' ? 1 : -1;
  const valueOf = (p: PostMinimal): number | null => {
    if (field === 'score') return readPostScore(p);
    if (field === 'rank') return readPostRank(p);
    return readPostMillis(p);
  };
  return list
    .map((p, i) => ({ p, i, v: valueOf(p) }))
    .sort((a, b) => {
      if (a.v === null && b.v === null) return a.i - b.i;
      if (a.v === null) return 1;
      if (b.v === null) return -1;
      if (a.v !== b.v) return (a.v - b.v) * dir;
      return a.i - b.i;
    })
    .map((x) => x.p);
}

/* ===== 高レベル helper (両画面共有) ===== */

/** 全 axisOptions を組み立てる (master + postField + custom − hidden)。 */
export function buildAxisOptions(args: {
  resolver: AiCategoryMastersResult;
  postFieldAxes: { key: string; label: string }[];
  customAxes: { key: string; label: string }[];
  hiddenAxisKeys?: string[] | undefined;
  /** docs/29 §8-1: 追加の観点 (あれば観点ごとの AI 解析の軸を足す)。 */
  perspectives?: readonly AnalysisPerspective[];
  /** 既定の観点の表示名 (追加の観点があるとき、既定の観点の AI 解析の軸に付ける)。 */
  defaultPerspectiveLabel?: string | null;
}): { key: AxisKey; label: string }[] {
  const { resolver, postFieldAxes, customAxes, hiddenAxisKeys } = args;
  const all = [
    ...buildAiAxisOptionsWithPerspectives(
      resolver,
      args.perspectives,
      args.defaultPerspectiveLabel,
    ),
    ...postFieldAxes,
    ...customAxes,
  ];
  if (hiddenAxisKeys && hiddenAxisKeys.length > 0) {
    const hideSet = new Set(hiddenAxisKeys);
    return all.filter((o) => !hideSet.has(o.key));
  }
  return all;
}

/** 軸 1 本の AxisGroup[] を解決 (postField / 既定 master / rule のいずれか)。includeEmpty 反映。 */
export function buildAxisGroups(
  axisKey: AxisKey,
  args: {
    resolver: AiCategoryMastersResult;
    posts: PostMinimal[];
    isVertical: boolean;
    postFieldAxisByKey: Map<string, { field: FieldDef; groups: PostFieldAxisGroup[] }>;
    customRuleByKey: Map<string, AnalysisAxisRule>;
    includeEmptyKeys: Set<string>;
    perspectives?: readonly AnalysisPerspective[];
  },
): AxisGroup[] {
  // docs/29 §8-1: 追加の観点の軸は、その観点のカテゴリマスタで分類する。
  const pa = resolvePerspectiveAxis(axisKey, args.perspectives);
  if (pa.perspective) {
    const base = isDefaultAxisKey(pa.baseKey)
      ? extractDefaultAxis(pa.perspective.resolver, pa.baseKey)
      : [];
    return appendEmptyBucket(base, args.includeEmptyKeys.has(axisKey));
  }
  const { resolver, posts, isVertical, postFieldAxisByKey, customRuleByKey, includeEmptyKeys } =
    args;
  const postFieldEntry = postFieldAxisByKey.get(axisKey);
  const base = isPostFieldAxisKey(axisKey)
    ? orientPostFieldAxisGroups(postFieldEntry?.field, postFieldEntry?.groups ?? [], isVertical)
    : isDefaultAxisKey(axisKey)
      ? extractDefaultAxis(resolver, axisKey)
      : (() => {
          const rule = customRuleByKey.get(axisKey);
          return rule ? extractRuleAxis(rule, resolver, posts, isVertical) : [];
        })();
  return appendEmptyBucket(base, includeEmptyKeys.has(axisKey));
}

/** post → 指定軸の code 集合 (mean-split は groups 経由、それ以外は postToCodes)。includeEmpty 反映。 */
export function postCodesForAxis(
  post: PostMinimal,
  axisKey: AxisKey,
  groups: AxisGroup[],
  args: {
    resolver: AiCategoryMastersResult;
    outputFormat: AiOutputFormat | null | undefined;
    postFieldAxisByKey: Map<string, { field: FieldDef; groups: PostFieldAxisGroup[] }>;
    customRuleByKey: Map<string, AnalysisAxisRule>;
    includeEmptyKeys: Set<string>;
    perspectives?: readonly AnalysisPerspective[];
  },
): Set<string> {
  const { resolver, outputFormat, postFieldAxisByKey, customRuleByKey, includeEmptyKeys } = args;
  // docs/29 §8-1: 追加の観点の軸は、その観点の結果に写した投稿・出力フォーマット・マスタで値を出す。
  const pa = resolvePerspectiveAxis(axisKey, args.perspectives);
  if (pa.perspective) {
    const codes = perspectiveAxisCodes(post, pa.perspective, pa.baseKey);
    if (codes.size === 0 && includeEmptyKeys.has(axisKey)) return new Set([EMPTY_AXIS_CODE]);
    return codes;
  }
  const isDefault = isDefaultAxisKey(axisKey);
  const isPostField = isPostFieldAxisKey(axisKey);
  const rule = !isDefault && !isPostField ? (customRuleByKey.get(axisKey) ?? null) : null;
  const postFieldEntry = postFieldAxisByKey.get(axisKey) ?? null;
  let codes: Set<string>;
  if (rule && normalizeBucketMethod(rule) === 'mean-split') {
    codes = postToMeanSplitCodes(post, rule, groups);
  } else {
    codes = postToCodes(post, axisKey, resolver, rule, outputFormat, postFieldEntry);
  }
  if (codes.size === 0 && includeEmptyKeys.has(axisKey)) {
    return new Set([EMPTY_AXIS_CODE]);
  }
  return codes;
}

/* ===== docs/29 §8-1: AI 解析の観点 ===== */

/**
 * AI 解析の軸の候補 (既定の観点 + 追加の観点)。追加の観点が無ければ従来どおり既定の観点の軸だけ (ラベルも従来どおり)。
 * 追加の観点があるときは、どの観点の軸か分かるよう既定の観点の軸にも観点名を付ける。
 */
export function buildAiAxisOptionsWithPerspectives(
  resolver: AiCategoryMastersResult,
  perspectives: readonly AnalysisPerspective[] | undefined,
  defaultPerspectiveLabel: string | null | undefined,
): { key: AxisKey; label: string }[] {
  const base = deriveAxisOptionsFromMasters(resolver);
  if (!perspectives || perspectives.length === 0) return base;
  const out = defaultPerspectiveLabel
    ? base.map((o) => ({
        key: o.key,
        label: perspectiveAxisLabel(defaultPerspectiveLabel, o.label),
      }))
    : [...base];
  for (const p of perspectives) {
    for (const o of deriveAxisOptionsFromMasters(p.resolver)) {
      out.push({
        key: perspectiveAxisKey(p.key, o.key),
        label: perspectiveAxisLabel(p.label, o.label),
      });
    }
  }
  return out;
}

/** 追加の観点の軸の値 (その観点の結果に写した投稿で計算)。 */
export function perspectiveAxisCodes(
  post: PostMinimal,
  perspective: AnalysisPerspective,
  baseKey: AxisKey,
): Set<string> {
  if (!isDefaultAxisKey(baseKey)) return new Set();
  const projected = projectForPerspective(post, perspective);
  return postToCodes(projected, baseKey, perspective.resolver, null, perspective.outputFormat);
}

/** 追加の観点の軸で、その観点の解析が済んでいる投稿か (既定の軸の status=done 判定の観点版)。 */
export function isAnalyzedForPerspective(
  post: PostMinimal,
  perspective: AnalysisPerspective,
): boolean {
  return projectForPerspective(post, perspective).aiAnalysisStatus === 'done';
}
